import { Notification } from "@/utils/notificationUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { documentsSignApi } from "../../../modules/docsignature/api";
import { DocSignatureView } from "../../../modules/docsignature/enums";
import {
  DocSignatureFilterPageRequest,
  InitDocSignature,
  TokenAccessDocSignature,
} from "../../../modules/docsignature/types";
import { EntityObject, ThreeLevelEntityIdObject, TwoLevelEntityIdObject, TwoLevelEntityObject } from "../../types";
import { CONTRACTS_QUERY_KEYS } from "./contract/contracts";

export const DOCUMENTS_SIGN_QUERY_KEYS = {
  createDocumentSignature: () => ["createDocumentSignature"],
  filterDocumentsToSignForClient: (
    payload?: EntityObject<DocSignatureFilterPageRequest & { view: DocSignatureView }>,
  ) => (payload ? ["filterDocumentsToSignForClient", payload] : ["filterDocumentsToSignForClient"]),
  downloadOriginalDocumentThroughEmail: (payload: TokenAccessDocSignature) => [
    "downloadOriginalDocumentThroughEmail",
    payload,
  ],
  confirmEmailSignature: () => ["confirmEmailSignature"],
  resendDocumentSignature: () => ["resendDocumentSignature"],
  deleteDocumentSignature: () => ["deleteDocumentSignature"],
  assignSignedDocumentToContract: () => ["assignSignedDocumentToContract"],
};

export const useCreateDocumentSignature = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DOCUMENTS_SIGN_QUERY_KEYS.createDocumentSignature(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<FormData>) => {
      const { data } = await documentsSignApi.createDocumentSignature(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_SIGN_QUERY_KEYS.filterDocumentsToSignForClient() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useFilterDocumentsToSignForClient = (
  payload: EntityObject<DocSignatureFilterPageRequest & { view: DocSignatureView }>,
  enabled?: boolean,
) => {
  return useQuery({
    queryKey: DOCUMENTS_SIGN_QUERY_KEYS.filterDocumentsToSignForClient(payload),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await documentsSignApi.filterDocumentsToSignForClient(payload);

      return data;
    },
    enabled,
  });
};

export const useDownloadOriginalDocumentThroughEmail = (payload: TokenAccessDocSignature) => {
  return useQuery({
    queryKey: DOCUMENTS_SIGN_QUERY_KEYS.downloadOriginalDocumentThroughEmail(payload),
    queryFn: async () => {
      const { data } = await documentsSignApi.downloadOriginalDocumentThroughEmail(payload);

      return data;
    },
    enabled: !!payload.token,
    retry: 1,
    refetchOnWindowFocus: false,
  });
};

export const useConfirmEmailSignature = () => {
  return useMutation({
    mutationKey: DOCUMENTS_SIGN_QUERY_KEYS.confirmEmailSignature(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TokenAccessDocSignature) => {
      const { data } = await documentsSignApi.confirmEmailSignature(payload);

      return data;
    },
    onSuccess: () => {
      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("common.createOperationSuccess"),
      });
    },
  });
};

export const useResendDocumentSignature = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DOCUMENTS_SIGN_QUERY_KEYS.resendDocumentSignature(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<InitDocSignature>) => {
      const { data } = await documentsSignApi.resendDocumentSignature(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_SIGN_QUERY_KEYS.filterDocumentsToSignForClient() });
      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("common.createOperationSuccess"),
      });
    },
  });
};

export const useDeleteDocumentSignature = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DOCUMENTS_SIGN_QUERY_KEYS.deleteDocumentSignature(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return documentsSignApi.deleteDocumentSignature(payload);
    },
    onSuccess: () => {
      Notification.itemDeletedNotification();
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_SIGN_QUERY_KEYS.filterDocumentsToSignForClient() });
    },
  });
};

export const useAssignSignedDocumentToContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DOCUMENTS_SIGN_QUERY_KEYS.assignSignedDocumentToContract(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: ThreeLevelEntityIdObject) => {
      const { data } = await documentsSignApi.assignSignedDocumentToContract(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.getContract(mutatedPayload.id3) });
      Notification.itemUpdatedNotification();
    },
  });
};
